import { Activity, RefreshCw, Wifi } from 'lucide-react';
import { DisplayActivityCard } from './DisplayActivityCard';

interface ActivityItem {
  id: string;
  type: string;
  message: string;
  teamId: string | null;
  teamName: string | null;
  teamIconName?: string;
  teamIconColor?: string;
  challengeId?: string | null;
  challengeName?: string | null;
  points?: number | null;
  photoUrl?: string | null;
  createdAt: string;
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface DisplayActivityFeedProps {
  activities: ActivityItem[];
  isConnected?: boolean;
  connectionStatus?: ConnectionStatus;
  newActivityIds?: Set<string>;
  pointsLabel: string;
}

/**
 * Live activity column of the display page, styled with the deck's
 * mono eyebrows and surface cards on the branded stage (colors come
 * from the `--dsp-*` variables set on the display root).
 */
export function DisplayActivityFeed({
  activities,
  isConnected,
  connectionStatus = 'connecting',
  newActivityIds,
  pointsLabel,
}: DisplayActivityFeedProps) {
  // Filter to only show challenge completions
  const filteredActivities = activities.filter((a) => a.type === 'challenge_completed');

  const live = isConnected || connectionStatus === 'connected';
  const StatusIcon = live ? Wifi : RefreshCw;
  const statusText = live
    ? 'Live'
    : connectionStatus === 'connecting'
      ? 'Connecting…'
      : 'Auto-refresh';

  return (
    <div className="flex h-full flex-col">
      {/* Section header — fixed height so it lines up exactly with the
          Standings header across the split. */}
      <div
        className="flex h-14 flex-shrink-0 items-center justify-between border-b px-6"
        style={{ borderColor: 'var(--dsp-line)' }}
      >
        <p
          className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.2em]"
          style={{ color: 'var(--dsp-accent-text)' }}
        >
          Live Activity
        </p>

        <div
          className="flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[0.625rem] uppercase tracking-[0.15em]"
          style={{
            borderColor: 'var(--dsp-line)',
            color: live ? 'var(--dsp-accent-text)' : 'var(--dsp-soft)',
          }}
        >
          <span className="relative flex h-1.5 w-1.5">
            {live && (
              <span
                className="dsp-animated absolute h-1.5 w-1.5 rounded-full"
                style={{
                  backgroundColor: 'var(--dsp-accent)',
                  animation: 'dspPinPulse 2.6s ease-in-out infinite',
                }}
              />
            )}
            <span
              className="relative h-1.5 w-1.5 rounded-full"
              style={{
                backgroundColor: live ? 'var(--dsp-accent)' : 'var(--dsp-soft)',
              }}
            />
          </span>
          <StatusIcon className={connectionStatus === 'connecting' ? 'h-3 w-3 animate-spin' : 'h-3 w-3'} />
          {statusText}
        </div>
      </div>

      {/* Activity list */}
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        {filteredActivities.length === 0 ? (
          <div
            className="flex h-full flex-col items-center justify-center gap-3"
            style={{ color: 'var(--dsp-soft)' }}
          >
            <Activity className="h-12 w-12 opacity-40" />
            <p className="text-2xl font-semibold" style={{ color: 'var(--dsp-text)' }}>
              No activity yet
            </p>
            <p className="font-mono text-xs uppercase tracking-[0.2em]">
              Challenge completions appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredActivities.map((activity) => (
              <DisplayActivityCard
                key={activity.id}
                activity={activity}
                isNew={newActivityIds?.has(activity.id)}
                pointsLabel={pointsLabel}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
